import React from "react";

import { User, Building } from "icons";
import * as R from "ramda";
import * as yup from "yup";

import { Tooltip } from "clearsense-ui/components";
import { LinkText } from "components/Common/TableStyle";
import expandAffirmationCode from "utils/expandAffirmationCode";
import getFullAddress from "utils/getFullAddress";

export const DEFAULT_PAGE_SIZE = 10;

export const NPI_TYPE = [
  { label: "Any", value: "" },
  { label: "Organization", value: "2" },
  { label: "Individual", value: "1" },
];

export const ADDRESS_TYPE = [
  { label: "Any", value: "" },
  { label: "Primary Location", value: "primary" },
  { label: "Secondary Location", value: "secondary" },
];

export const NPI_REGISTRY_FORM_VALIDATIONS = yup.object().shape({
  npi: yup
    .number()
    .min(
      1000000000,
      "You can only search by an exact NPI number, which is 10 digits long."
    )
    .max(
      10000000000,
      "You can only search by an exact NPI number, which is 10 digits long."
    )
    .typeError("NPI number must be digits"),
  firstName: yup
    .string()
    .test(
      "isFirstNameAndOrganizationNamePresent",
      "First Name and Organization Name cannot be searched together",
      (name, { parent: { organizationName } }) => !name || !organizationName
    ),
  lastName: yup
    .string()
    .test(
      "isLastNameAndOrganizationNamePresent",
      "Last Name and Organization Name cannot be searched together",
      (name, { parent: { organizationName } }) => !name || !organizationName
    ),
  organizationName: yup
    .string()
    .test(
      "isOrganizationNameAndIndividualNamePresent",
      "First Name/Last Name and Organization Name cannot be searched together",
      (name, { parent: { firstName, lastName } }) =>
        !name || !(firstName?.length || lastName?.length)
    ),
});

export const NPI_TABLE_COLUMNS = [
  {
    dataIndex: "npi",
    title: "NPI",
    render: npi => <LinkText text={npi} link={npi} textSyle="h5" />,
    width: 121,
  },
  {
    dataIndex: "entity_type_code",
    title: "NPI Type",
    align: "center",
    render: code => (
      <Tooltip
        position="top"
        content={code === 1 ? "Individual" : "Organization"}
      >
        <div className="flex justify-center">
          {code === 1 ? <User size={22} /> : <Building size={22} />}
        </div>
      </Tooltip>
    ),
    width: 100,
  },
  {
    title: "Name",
    render: (
      _,
      {
        entity_type_code,
        provider_first_name,
        provider_middle_name,
        provider_last_name,
        provider_organization_name,
      }
    ) =>
      entity_type_code === 1
        ? `${provider_first_name} ${provider_middle_name} ${provider_last_name}`
        : provider_organization_name,
  },
  {
    title: "Primary Practice Address",
    render: (_, record) =>
      getFullAddress(
        R.props(
          [
            "provider_first_line_business_practice_location_address",
            "provider_second_line_business_practice_location_address",
            "provider_business_practice_location_address_city_name",
            "provider_business_practice_location_address_state_name",
            "provider_business_practice_location_address_postal_code",
          ],
          record
        ),
        [" ", "\n", ", ", " "]
      ),
  },
  {
    title: "Primary Taxonomy",
    dataIndex: "primary_taxonomy_description",
    render: description => description || "No Primary Taxonomy",
  },
];

export const COUNTRY_CODES = [
  {
    label: "Any",
    value: "",
  },
  {
    label: "Afghanistan",
    value: "AF",
  },
  {
    label: "Albania",
    value: "AL",
  },
  {
    label: "Algeria",
    value: "DZ",
  },
  {
    label: "Andorra",
    value: "AD",
  },
  {
    label: "Angola",
    value: "AO",
  },
  {
    label: "Anguilla",
    value: "AI",
  },
  {
    label: "Antarctica",
    value: "AQ",
  },
  {
    label: "Antigua and Barbuda",
    value: "AG",
  },
  {
    label: "Argentina",
    value: "AR",
  },
  {
    label: "Armenia",
    value: "AM",
  },
  {
    label: "Aruba",
    value: "AW",
  },
  {
    label: "Australia",
    value: "AU",
  },
  {
    label: "Austria",
    value: "AT",
  },
  {
    label: "Azerbaijan",
    value: "AZ",
  },
  {
    label: "Bahamas",
    value: "BS",
  },
  {
    label: "Bahrain",
    value: "BH",
  },
  {
    label: "Bangladesh",
    value: "BD",
  },
  {
    label: "Barbados",
    value: "BB",
  },
  {
    label: "Belarus",
    value: "BY",
  },
  {
    label: "Belgium",
    value: "BE",
  },
  {
    label: "Belize",
    value: "BZ",
  },
  {
    label: "Benin",
    value: "BJ",
  },
  {
    label: "Bermuda",
    value: "BM",
  },
  {
    label: "Bhutan",
    value: "BT",
  },
  {
    label: "Bolivia",
    value: "BO",
  },
  {
    label: "Bosnia and Herzegovina",
    value: "BA",
  },
  {
    label: "Botswana",
    value: "BW",
  },
  {
    label: "Bouvet Island",
    value: "BV",
  },
  {
    label: "Brazil",
    value: "BR",
  },
  {
    label: "British Indian Ocean Territory",
    value: "IO",
  },
  {
    label: "Brunei Darussalam",
    value: "BN",
  },
  {
    label: "Bulgaria",
    value: "BG",
  },
  {
    label: "Burkina Faso",
    value: "BF",
  },
  {
    label: "Burundi",
    value: "BI",
  },
  {
    label: "Cambodia",
    value: "KH",
  },
  {
    label: "Cameroon",
    value: "CM",
  },
  {
    label: "Canada",
    value: "CA",
  },
  {
    label: "Cape Verde",
    value: "CV",
  },
  {
    label: "Cayman Islands",
    value: "KY",
  },
  {
    label: "Central African Republic",
    value: "CF",
  },
  {
    label: "Chad",
    value: "TD",
  },
  {
    label: "Chile",
    value: "CL",
  },
  {
    label: "China",
    value: "CN",
  },
  {
    label: "Christmas Island",
    value: "CX",
  },
  {
    label: "Cocos (Keeling) Islands",
    value: "CC",
  },
  {
    label: "Colombia",
    value: "CO",
  },
  {
    label: "Comoros",
    value: "KM",
  },
  {
    label: "Congo, The Democratic Republic of the",
    value: "CD",
  },
  {
    label: "Cook Islands",
    value: "CK",
  },
  {
    label: "Costa Rica",
    value: "CR",
  },
  {
    label: "Croatia",
    value: "HR",
  },
  {
    label: "Ctte D'Ivoire",
    value: "CI",
  },
  {
    label: "Cuba",
    value: "CU",
  },
  {
    label: "Cyprus",
    value: "CY",
  },
  {
    label: "Czech Republic",
    value: "CZ",
  },
  {
    label: "Denmark",
    value: "DK",
  },
  {
    label: "Djibouti",
    value: "DJ",
  },
  {
    label: "Dominica",
    value: "DM",
  },
  {
    label: "Dominican Republic",
    value: "DO",
  },
  {
    label: "Ecuador",
    value: "EC",
  },
  {
    label: "Egypt",
    value: "EG",
  },
  {
    label: "El Salvador",
    value: "SV",
  },
  {
    label: "Equatorial Guinea",
    value: "GQ",
  },
  {
    label: "Eritrea",
    value: "ER",
  },
  {
    label: "Estonia",
    value: "EE",
  },
  {
    label: "Ethiopia",
    value: "ET",
  },
  {
    label: "Falkland Islands (Malvinas)",
    value: "FK",
  },
  {
    label: "Faroe Islands",
    value: "FO",
  },
  {
    label: "Fiji",
    value: "FJ",
  },
  {
    label: "Finland",
    value: "FI",
  },
  {
    label: "France",
    value: "FR",
  },
  {
    label: "French Guiana",
    value: "GF",
  },
  {
    label: "French Polynesia",
    value: "PF",
  },
  {
    label: "French Southern Territories",
    value: "TF",
  },
  {
    label: "Gabon",
    value: "GA",
  },
  {
    label: "Gambia",
    value: "GM",
  },
  {
    label: "Georgia",
    value: "GE",
  },
  {
    label: "Germany",
    value: "DE",
  },
  {
    label: "Ghana",
    value: "GH",
  },
  {
    label: "Gibraltar",
    value: "GI",
  },
  {
    label: "Great Britain",
    value: "GB",
  },
  {
    label: "Greece",
    value: "GR",
  },
  {
    label: "Greenland",
    value: "GL",
  },
  {
    label: "Grenada",
    value: "GD",
  },
  {
    label: "Guadeloupe",
    value: "GP",
  },
  {
    label: "Guatemala",
    value: "GT",
  },
  {
    label: "Guernsey",
    value: "GG",
  },
  {
    label: "Guinea",
    value: "GN",
  },
  {
    label: "Guinea-Bissau",
    value: "GW",
  },
  {
    label: "Guyana",
    value: "GY",
  },
  {
    label: "Haiti",
    value: "HT",
  },
  {
    label: "Heard Island and McDonald Islands",
    value: "HM",
  },
  {
    label: "Holy See (Vatican City State)",
    value: "VA",
  },
  {
    label: "Honduras",
    value: "HN",
  },
  {
    label: "Hong Kong",
    value: "HK",
  },
  {
    label: "Hungary",
    value: "HU",
  },
  {
    label: "Iceland",
    value: "IS",
  },
  {
    label: "India",
    value: "IN",
  },
  {
    label: "Indonesia",
    value: "ID",
  },
  {
    label: "Iran, Islamic Republic of",
    value: "IR",
  },
  {
    label: "Iraq",
    value: "IQ",
  },
  {
    label: "Ireland",
    value: "IE",
  },
  {
    label: "Isle Of Man",
    value: "IM",
  },
  {
    label: "Israel",
    value: "IL",
  },
  {
    label: "Italy",
    value: "IT",
  },
  {
    label: "Jamaica",
    value: "JM",
  },
  {
    label: "Japan",
    value: "JP",
  },
  {
    label: "Jersey",
    value: "JE",
  },
  {
    label: "Jordan",
    value: "JO",
  },
  {
    label: "Kazakhstan",
    value: "KZ",
  },
  {
    label: "Kenya",
    value: "KE",
  },
  {
    label: "Kiribati",
    value: "KI",
  },
  {
    label: "Korea, Democratic People's Republic of",
    value: "KP",
  },
  {
    label: "Korea, Republic of",
    value: "KR",
  },
  {
    label: "Kosovo",
    value: "XK",
  },
  {
    label: "Kuwait",
    value: "KW",
  },
  {
    label: "Kyrgyzstan",
    value: "KG",
  },
  {
    label: "Lao People's Democratic Republic",
    value: "LA",
  },
  {
    label: "Latvia",
    value: "LV",
  },
  {
    label: "Lebanon",
    value: "LB",
  },
  {
    label: "Lesotho",
    value: "LS",
  },
  {
    label: "Liberia",
    value: "LR",
  },
  {
    label: "Libyan Arab Jamahiriya",
    value: "LY",
  },
  {
    label: "Liechtenstein",
    value: "LI",
  },
  {
    label: "Lithuania",
    value: "LT",
  },
  {
    label: "Luxembourg",
    value: "LU",
  },
  {
    label: "Macao",
    value: "MO",
  },
  {
    label: "Macedonia, The Former Yugoslav Republic of",
    value: "MK",
  },
  {
    label: "Madagascar",
    value: "MG",
  },
  {
    label: "Malawi",
    value: "MW",
  },
  {
    label: "Malaysia",
    value: "MY",
  },
  {
    label: "Maldives",
    value: "MV",
  },
  {
    label: "Mali",
    value: "ML",
  },
  {
    label: "Malta",
    value: "MT",
  },
  {
    label: "Martinique",
    value: "MQ",
  },
  {
    label: "Mauritania",
    value: "MR",
  },
  {
    label: "Mauritius",
    value: "MU",
  },
  {
    label: "Mayotte",
    value: "YT",
  },
  {
    label: "Mexico",
    value: "MX",
  },
  {
    label: "Moldova, Republic of",
    value: "MD",
  },
  {
    label: "Monaco",
    value: "MC",
  },
  {
    label: "Mongolia",
    value: "MN",
  },
  {
    label: "Montserrat",
    value: "MS",
  },
  {
    label: "Morocco",
    value: "MA",
  },
  {
    label: "Mozambique",
    value: "MZ",
  },
  {
    label: "Myanmar",
    value: "MM",
  },
  {
    label: "Namibia",
    value: "NA",
  },
  {
    label: "Nauru",
    value: "NR",
  },
  {
    label: "Nepal",
    value: "NP",
  },
  {
    label: "Netherlands",
    value: "NL",
  },
  {
    label: "Netherlands Antilles",
    value: "AN",
  },
  {
    label: "New Caledonia",
    value: "NC",
  },
  {
    label: "New Zealand",
    value: "NZ",
  },
  {
    label: "Nicaragua",
    value: "NI",
  },
  {
    label: "Niger",
    value: "NE",
  },
  {
    label: "Nigeria",
    value: "NG",
  },
  {
    label: "Niue",
    value: "NU",
  },
  {
    label: "Norfolk Island",
    value: "NF",
  },
  {
    label: "Norway",
    value: "NO",
  },
  {
    label: "Oman",
    value: "OM",
  },
  {
    label: "Pakistan",
    value: "PK",
  },
  {
    label: "Palau",
    value: "PW",
  },
  {
    label: "Palestinian Territory, Occupied",
    value: "PS",
  },
  {
    label: "Panama",
    value: "PA",
  },
  {
    label: "Papua New Guinea",
    value: "PG",
  },
  {
    label: "Paraguay",
    value: "PY",
  },
  {
    label: "Peru",
    value: "PE",
  },
  {
    label: "Philippines",
    value: "PH",
  },
  {
    label: "Pitcairn",
    value: "PN",
  },
  {
    label: "Poland",
    value: "PL",
  },
  {
    label: "Portugal",
    value: "PT",
  },
  {
    label: "Qatar",
    value: "QA",
  },
  {
    label: "Reunion",
    value: "RE",
  },
  {
    label: "Romania",
    value: "RO",
  },
  {
    label: "Russian Federation",
    value: "RU",
  },
  {
    label: "Rwanda",
    value: "RW",
  },
  {
    label: "Saint Helena",
    value: "SH",
  },
  {
    label: "Saint Kitts and Nevis",
    value: "KN",
  },
  {
    label: "Saint Lucia",
    value: "LC",
  },
  {
    label: "Saint Pierre and Miquelon",
    value: "PM",
  },
  {
    label: "Saint Vincent and the Grenadines",
    value: "VC",
  },
  {
    label: "Samoa",
    value: "WS",
  },
  {
    label: "San Marino",
    value: "SM",
  },
  {
    label: "Sao Tome and Principe",
    value: "ST",
  },
  {
    label: "Saudi Arabia",
    value: "SA",
  },
  {
    label: "Senegal",
    value: "SN",
  },
  {
    label: "Serbia and Montenegro",
    value: "CS",
  },
  {
    label: "Seychelles",
    value: "SC",
  },
  {
    label: "Sierra Leone",
    value: "SL",
  },
  {
    label: "Singapore",
    value: "SG",
  },
  {
    label: "Slovakia",
    value: "SK",
  },
  {
    label: "Slovenia",
    value: "SI",
  },
  {
    label: "Solomon Islands",
    value: "SB",
  },
  {
    label: "Somalia",
    value: "SO",
  },
  {
    label: "South Africa",
    value: "ZA",
  },
  {
    label: "South Georgia and the South Sandwich Islands",
    value: "GS",
  },
  {
    label: "Spain",
    value: "ES",
  },
  {
    label: "Sri Lanka",
    value: "LK",
  },
  {
    label: "Sudan",
    value: "SD",
  },
  {
    label: "Suriname",
    value: "SR",
  },
  {
    label: "Svalbard and Jan Mayen",
    value: "SJ",
  },
  {
    label: "Swaziland",
    value: "SZ",
  },
  {
    label: "Sweden",
    value: "SE",
  },
  {
    label: "Switzerland",
    value: "CH",
  },
  {
    label: "Syrian Arab Republic",
    value: "SY",
  },
  {
    label: "Taiwan",
    value: "TW",
  },
  {
    label: "Tajikistan",
    value: "TJ",
  },
  {
    label: "Tanzania, United Republic of",
    value: "TZ",
  },
  {
    label: "Thailand",
    value: "TH",
  },
  {
    label: "Timor-Leste",
    value: "TL",
  },
  {
    label: "Togo",
    value: "TG",
  },
  {
    label: "Tokelau",
    value: "TK",
  },
  {
    label: "Tonga",
    value: "TO",
  },
  {
    label: "Trinidad and Tobago",
    value: "TT",
  },
  {
    label: "Tunisia",
    value: "TN",
  },
  {
    label: "Turkey",
    value: "TR",
  },
  {
    label: "Turkmenistan",
    value: "TM",
  },
  {
    label: "Turks and Caicos Islands",
    value: "TC",
  },
  {
    label: "Tuvalu",
    value: "TV",
  },
  {
    label: "Uganda",
    value: "UG",
  },
  {
    label: "Ukraine",
    value: "UA",
  },
  {
    label: "United Arab Emirates",
    value: "AE",
  },
  {
    label: "United States",
    value: "US",
  },
  {
    label: "United States Minor Outlying Islands",
    value: "UM",
  },
  {
    label: "Uruguay",
    value: "UY",
  },
  {
    label: "Uzbekistan",
    value: "UZ",
  },
  {
    label: "Vanuatu",
    value: "VU",
  },
  {
    label: "Venezuela",
    value: "VE",
  },
  {
    label: "Viet Nam",
    value: "VN",
  },
  {
    label: "Virgin Islands, British",
    value: "VG",
  },
  {
    label: "Wallis and Futuna",
    value: "WF",
  },
  {
    label: "Western Sahara",
    value: "EH",
  },
  {
    label: "Yemen",
    value: "YE",
  },
  {
    label: "Zambia",
    value: "ZM",
  },
  {
    label: "Zimbabwe",
    value: "ZW",
  },
];

export const STATE_CODES = [
  {
    label: "Any",
    value: "",
  },
  {
    label: "Alabama",
    value: "AL",
  },
  {
    label: "Alaska",
    value: "AK",
  },
  {
    label: "American Samoa",
    value: "AS",
  },
  {
    label: "Arizona",
    value: "AZ",
  },
  {
    label: "Arkansas",
    value: "AR",
  },
  {
    label: "Armed Forces America",
    value: "AA",
  },
  {
    label: "Armed Forces Europe /Canada / Middle East / Africa",
    value: "AE",
  },
  {
    label: "Armed Forces Pacific",
    value: "AP",
  },
  {
    label: "California",
    value: "CA",
  },
  {
    label: "Colorado",
    value: "CO",
  },
  {
    label: "Connecticut",
    value: "CT",
  },
  {
    label: "Delaware",
    value: "DE",
  },
  {
    label: "District of Columbia",
    value: "DC",
  },
  {
    label: "Federated States of Micronesia",
    value: "FM",
  },
  {
    label: "Florida",
    value: "FL",
  },
  {
    label: "Georgia",
    value: "GA",
  },
  {
    label: "Guam",
    value: "GU",
  },
  {
    label: "Hawaii",
    value: "HI",
  },
  {
    label: "Idaho",
    value: "ID",
  },
  {
    label: "Illinois",
    value: "IL",
  },
  {
    label: "Indiana",
    value: "IN",
  },
  {
    label: "Iowa",
    value: "IA",
  },
  {
    label: "Kansas",
    value: "KS",
  },
  {
    label: "Kentucky",
    value: "KY",
  },
  {
    label: "Louisiana",
    value: "LA",
  },
  {
    label: "Maine",
    value: "ME",
  },
  {
    label: "Mariana Islands, Northern",
    value: "MP",
  },
  {
    label: "Marshall Islands",
    value: "MH",
  },
  {
    label: "Maryland",
    value: "MD",
  },
  {
    label: "Massachusetts",
    value: "MA",
  },
  {
    label: "Michigan",
    value: "MI",
  },
  {
    label: "Minnesota",
    value: "MN",
  },
  {
    label: "Mississippi",
    value: "MS",
  },
  {
    label: "Missouri",
    value: "MO",
  },
  {
    label: "Montana",
    value: "MT",
  },
  {
    label: "Nebraska",
    value: "NE",
  },
  {
    label: "Nevada",
    value: "NV",
  },
  {
    label: "New Hampshire",
    value: "NH",
  },
  {
    label: "New Jersey",
    value: "NJ",
  },
  {
    label: "New Mexico",
    value: "NM",
  },
  {
    label: "New York",
    value: "NY",
  },
  {
    label: "North Carolina",
    value: "NC",
  },
  {
    label: "North Dakota",
    value: "ND",
  },
  {
    label: "Ohio",
    value: "OH",
  },
  {
    label: "Oklahoma",
    value: "OK",
  },
  {
    label: "Oregon",
    value: "OR",
  },
  {
    label: "Pennsylvania",
    value: "PA",
  },
  {
    label: "Puerto Rico",
    value: "PR",
  },
  {
    label: "Rhode Island",
    value: "RI",
  },
  {
    label: "South Carolina",
    value: "SC",
  },
  {
    label: "South Dakota",
    value: "SD",
  },
  {
    label: "Tennessee",
    value: "TN",
  },
  {
    label: "Texas",
    value: "TX",
  },
  {
    label: "Utah",
    value: "UT",
  },
  {
    label: "Vermont",
    value: "VT",
  },
  {
    label: "Virgin islands",
    value: "VI",
  },
  {
    label: "Virginia",
    value: "VA",
  },
  {
    label: "Washington",
    value: "WA",
  },
  {
    label: "West Virginia",
    value: "WV",
  },
  {
    label: "Wisconsin",
    value: "WI",
  },
  {
    label: "Wyoming",
    value: "WY",
  },
];

export const INITIAL_NPI_REGISTRY_FORM_VALUES = {
  npi: "",
  entityTypeCode: NPI_TYPE[0],
  taxonomyDescription: "",
  firstName: "",
  lastName: "",
  organizationName: "",
  city: "",
  state: STATE_CODES[0],
  countryCode: COUNTRY_CODES[0],
  postalCode: "",
  addressType: ADDRESS_TYPE[0],
  exactMatch: false,
};

export const ADDRESS_VALUE_SEPARATORS = [
  " ",
  "\n",
  ", ",
  " ",
  "\n",
  "\n\nPhone: ",
  " | Fax: ",
];

export const HEALTH_INFORMATION_EXCHANGE_TABLE_COLUMNS = [
  {
    title: "Endpoint Type",
    dataIndex: "endpoint_type_description",
  },
  {
    title: "Endpoint",
    dataIndex: "endpoint",
  },
  {
    title: "Endpoint Description",
    dataIndex: "endpoint_description",
  },
  {
    title: "Use",
    dataIndex: "use_description",
  },
  {
    title: "Content Type",
    dataIndex: "content_description",
  },
  {
    title: "Affiliation",
    dataIndex: "affiliation_legal_business_name",
  },
  {
    title: "Endpoint Location",
    render: record =>
      `${record.affiliation_address_line_one} ${record.affiliation_address_line_two} ${record.affiliation_address_city}, ${record.affiliation_address_state} ${record.affiliation_address_postal_code} ${record.affiliation_address_country}`,
  },
];

export const OTHER_IDENTIFIER_TABLE_COLUMNS = [
  {
    title: "Issuer",
    render: ({
      other_provider_identifier_type_code,
      other_provider_identifier_issuer,
    }) =>
      (other_provider_identifier_type_code === "05" ? "Medicaid" : "Other") +
      (other_provider_identifier_issuer
        ? ` - ${other_provider_identifier_issuer}`
        : ""),
  },
  {
    title: "State",
    dataIndex: "other_provider_identifier_state",
  },
  {
    title: "Number",
    dataIndex: "other_provider_identifier",
  },
];

export const TAXONOMY_TABLE_COLUMNS = [
  {
    title: "Primary Taxonomy",
    dataIndex: "healthcare_provider_primary_taxonomy_switch",
    render: (_, { healthcare_provider_primary_taxonomy_switch }) =>
      expandAffirmationCode(healthcare_provider_primary_taxonomy_switch),
  },
  {
    title: "Selected Taxonomy",
    render: (
      _,
      {
        healthcare_provider_taxonomy_code,
        healthcare_provider_taxonomy_description,
      }
    ) =>
      healthcare_provider_taxonomy_code +
      (R.isEmpty(healthcare_provider_taxonomy_description)
        ? ""
        : ` - ${healthcare_provider_taxonomy_description}`),
  },
  {
    title: "State",
    dataIndex: "provider_license_number_state_code",
  },
  {
    title: "License Number",
    dataIndex: "provider_license_number",
  },
];

export const MAILING_ADDRESS_KEYS = [
  "provider_first_line_business_mailing_address",
  "provider_second_line_business_mailing_address",
  "provider_business_mailing_address_city_name",
  "provider_business_mailing_address_state_name",
  "provider_business_practice_location_address_postal_code",
  "provider_business_practice_location_address_country_code",
  "provider_business_mailing_address_telephone_number",
  "provider_business_mailing_address_fax_number",
];

export const PRIMARY_PRACTICE_ADDRESS_KEYS = [
  "provider_first_line_business_practice_location_address",
  "provider_second_line_business_practice_location_address",
  "provider_business_practice_location_address_city_name",
  "provider_business_practice_location_address_state_name",
  "provider_business_practice_location_address_postal_code",
  "provider_business_practice_location_address_country_code",
  "provider_business_practice_location_address_telephone_number",
  "provider_business_practice_location_address_fax_number",
];

export const SECONDARY_PRACTICE_ADDRESS_KEYS = [
  "provider_secondary_pl_address_address_line_1",
  "provider_secondary_pl_address_address_line_2",
  "provider_secondary_pl_address_city_name",
  "provider_secondary_pl_address_state_name",
  "provider_secondary_pl_address_postal_code",
  "provider_secondary_pl_address_country_code",
  "provider_secondary_pl_address_telephone_number",
  "provider_pl_address_fax_number",
];
